<script setup>
// The system map at three levels of detail. All: a few big group cards
// (Competition types, Playing, Running your club, Showing it off) and nothing
// else; click one to fly into it. Group: that group's areas, one card each
// (illustration, name, how many parts), with a few soft connectors between
// related areas; click a card to fly into the area. Area: that area's parts as
// cards in a top-down tree that wraps to the screen's width at a readable size;
// hover or select a part and its links to other areas appear as chips in free
// space beside it (a tray placed away from its card is tethered by a leader and
// a corridor the pointer may travel along); click a part for its dialog. What
// you can hover and click is decided by the level, never by what is under the
// cursor, and nothing lights up on entering an area until the pointer actually
// moves. Zoom is clamped per level (zooming on past the limit changes level),
// panning is bounded, the camera refits when the canvas resizes while it is at
// rest, and the three levels are separate layers so text is crisp at every
// zoom. Going deeper is a history entry each time (into a group, into an area,
// opening a part) so Back steps out one level at a time; the URL carries the
// group, area or selected part as #hash and the open part as ?node=. A search
// result or the Areas menu jumps straight into an area, passing through its
// group in the breadcrumb and the history. Used by /help/system-map and
// /embed/system-map.
// Data: utils/systemMap.js. Layout, placement and rules: utils/systemMapLayout.js.
import TlDialog from '~/components/TlDialog.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import SystemMapArt from '~/components/systemMap/SystemMapArt.vue';
import SystemMapArea from '~/components/systemMap/SystemMapArea.vue';
import SystemMapNode from '~/components/systemMap/SystemMapNode.vue';
import SystemMapChip from '~/components/systemMap/SystemMapChip.vue';
import SystemMapDialog from '~/components/systemMap/SystemMapDialog.vue';
import {useSystemMapViewport} from '~/composables/useSystemMapViewport';
import {
  SYSTEM_MAP_GROUPS, SYSTEM_MAP_NODES, crossAreaLinks, effectiveFlags, findSystemMapGroup, findSystemMapNode,
  findSystemMapSection, searchSystemMap, sectionTone, systemMapConnections, systemMapGroupOf, visibleSystemMapIds,
} from '~/utils/systemMap';
import {
  ALL_COLUMNS, ALL_COLUMNS_LANDSCAPE, ALL_COLUMNS_PORTRAIT, AREA_COUNT_PX, AREA_COUNT_PX_COMPACT, ENTRY_FLOOR_SCALE, ENTRY_HARD_FLOOR_SCALE, GROUP_MAX_COLUMNS, GROUP_MAX_COLUMNS_LANDSCAPE, LEVELS,
  OVERVIEW_COLUMNS_COMPACT, PART_LABEL_PX, READABLE_SCALE, TRAY_CAP_H, TRAY_FAR,
  areaEntryLayout, areaEntryView, areaLayoutWidth, cardAt, createLevelGate, hashAnchorId, historyAction, historySteps, interactivity, layoutAll, layoutArea,
  layoutGroup, leaderIsClear, leaderLine, overviewEntryView, partFocusView, prefitScript, overviewNameScale, overviewSections, overviewTypeScale, landscapeGroupTile, phoneGroupTile, SYSTEM_MAP_STORE_KEY, phoneTileHeight, placeTrayFor,
  pointInCorridor, routeFor, routeWants, sameView, scaleRange, toScreenBox, trayBounds, trayCorridor, TRAY_MAX,
} from '~/utils/systemMapLayout';

const props = defineProps({
  // Bare page for the app's web view: fills the viewport, no site chrome assumed.
  embedded: {type: Boolean, default: false},
  height: {type: String, default: 'calc(100dvh - 160px)'},
});

const route = useRoute();
const router = useRouter();

// ── The one checkbox (remembered per browser as a convenience) ────────────
const STORE_KEY = SYSTEM_MAP_STORE_KEY;
const showAdmin = ref(true);
const remember = () => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify({admin: showAdmin.value})); } catch (err) { /* storage unavailable */ }
};
const restore = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    if (saved && typeof saved.admin === 'boolean') showAdmin.value = saved.admin;
  } catch (err) { /* storage unavailable */ }
};

// ── Phone / desktop, level ────────────────────────────────────────────────
const isPhone = ref(false);
const mounted = ref(false);
const level = ref(LEVELS.ALL);
const groupId = ref(null);       // the entered group (kept while its layer fades out)
const areaId = ref(null);        // the entered area (kept while its layer fades out)
const inArea = computed(() => level.value === LEVELS.AREA);
const inGroup = computed(() => level.value === LEVELS.GROUP);
const atAll = computed(() => level.value === LEVELS.ALL);
const can = computed(() => interactivity(level.value));

// ── Viewport: one camera, three layers (the level decides which it drives) ─
const canvasEl = ref(null);
const allEl = ref(null);
const groupEl = ref(null);
const areaEl = ref(null);
const layerEl = (lvl) => (lvl === LEVELS.AREA ? areaEl.value : lvl === LEVELS.GROUP ? groupEl.value : allEl.value);
const activeLayer = computed(() => (inArea.value ? areaEl.value : inGroup.value ? groupEl.value : allEl.value));
// The bars lie over the canvas: keep what we fit clear of them. Their real
// heights are measured (the desktop bar grows a row inside an area for the
// area's line, the phone pill grows a row too), so no card or tray is ever
// placed under them (R53 M1) and a phone's first screen ends exactly at its
// bottom bar (R53 L3); the numbers here are the first guess before the measure.
const barEl = ref(null);
const phoneTopEl = ref(null);
const bottomEl = ref(null);
const barH = ref(0);
const pillH = ref(0);
const dockH = ref(0);
// The desktop bar inside an area (it carries the area's line as a second row): remembered from the last area visit so
// an area's layout can be chosen for its real first screen before it is entered (search pick, prewarm: R56 M1)
const areaBarH = ref(0);
const inset = computed(() => (isPhone.value
  ? {top: (pillH.value ? pillH.value + 10 : 54) + 6, bottom: dockH.value || 64}
  : {top: (barH.value || 64) + 4, bottom: 0}));
let barsRo = null;
const measureBars = () => {
  const h = (el) => (el ? Math.round(el.getBoundingClientRect().height) : 0);
  barH.value = h(barEl.value);
  if (inArea.value && barH.value) areaBarH.value = barH.value;
  pillH.value = h(phoneTopEl.value);
  dockH.value = h(bottomEl.value);
};
/** Watches the bars' sizes (they are swapped when the phone/desktop mode flips, so this is re-run then). */
const observeBars = () => {
  barsRo?.disconnect();
  measureBars();
  if (typeof ResizeObserver === 'undefined') return;
  barsRo = new ResizeObserver(measureBars);
  for (const el of [barEl.value, phoneTopEl.value, bottomEl.value]) if (el) barsRo.observe(el);
};
const PAD = computed(() => (isPhone.value ? 16 : 28));
const AREA_PAD = computed(() => (isPhone.value ? 16 : 24));
// A phone reads parts at full size; a desktop at the readable scale. An entry
// view may go a little under (to where a line is still 11px) to fit an area
// whole or to land the screen's bottom edge in a gap, and a little over for
// the same reason (a phone: nearer full size either way).
const readable = computed(() => (isPhone.value ? 1 : READABLE_SCALE));
const entryFloor = computed(() => (isPhone.value ? 0.94 : ENTRY_FLOOR_SCALE));
// Only when no scale from the floor up lands the bottom edge in a gap may a desktop entry go a little smaller (R56 M1)
const entryHardFloor = computed(() => (isPhone.value ? entryFloor.value : ENTRY_HARD_FLOOR_SCALE));
const entryCeiling = computed(() => (isPhone.value ? 1.1 : 1));

// One level change at a time (R53 H1): a zoom that steps a level, a Back the
// page asked the browser for, the Back arrow, Esc, Backspace and the phone pill
// all go through the gate, which stays shut until the change has landed (the
// route watcher has applied the pop, the fly-in has started). The zoom input
// itself is latched per burst by the viewport's stepper.
const gate = createLevelGate();
const gated = (make) => {
  if (!gate.begin()) return Promise.resolve();
  let run = null;
  try { run = make(); } finally { if (!run) gate.end(); }
  return run ? Promise.resolve(run).finally(() => gate.end()) : Promise.resolve();
};
const onOverscroll = (action, point) => gated(() => {
  const m = vp.toMap(point.x, point.y);
  if (action === 'enter' && atAll.value) {
    const id = cardAt(all.value, m.x, m.y);
    return id ? enterGroup(id) : null;
  }
  if (action === 'enter' && inGroup.value) {
    const id = cardAt(group.value, m.x, m.y);
    return id ? enterArea(id) : null;
  }
  if (action === 'leave' && inArea.value) return leaveArea();
  if (action === 'leave' && inGroup.value) return leaveGroup();
  return null;
});
const pointer = {x: 0, y: 0};
const onPointer = (p) => {
  pointer.x = p.x;
  pointer.y = p.y;
  if (!hoverArmed.value) {
    // Nothing lights up on entering an area until the pointer really moves
    if (!armAt || Math.hypot(p.x - armAt.x, p.y - armAt.y) < 6) return;
    armHover();
    return;
  }
  checkTravel();
};
const vp = useSystemMapViewport({canvasEl, layerEl: activeLayer, onOverscroll, onPointer});

// ── Data and layouts ──────────────────────────────────────────────────────
const flagsById = new Map(SYSTEM_MAP_NODES.map((n) => [n.id, effectiveFlags(n)]));
const visibleIds = computed(() => visibleSystemMapIds({admin: showAdmin.value}));
// A phone on its side lays its cards out like a desktop (the groups in one row, areas three across), so the all
// level and every group fit whole between the pill and the bar (R53 M4); upright, the groups are one column of tall
// tiles and a group's tiles grow to fill the band (R53 L1)
const landscape = computed(() => isPhone.value && vp.size.w > vp.size.h);
const allColumns = computed(() => (!isPhone.value ? ALL_COLUMNS : landscape.value ? ALL_COLUMNS_LANDSCAPE : ALL_COLUMNS_PORTRAIT));
const groupColumns = computed(() => (!isPhone.value ? GROUP_MAX_COLUMNS : landscape.value ? GROUP_MAX_COLUMNS_LANDSCAPE : OVERVIEW_COLUMNS_COMPACT));
const all = computed(() => layoutAll({visible: visibleIds.value, columns: allColumns.value, compact: isPhone.value}));
const group = computed(() => {
  if (!groupId.value) return null;
  const opts = {visible: visibleIds.value, columns: groupColumns.value, compact: isPhone.value, balanced: !isPhone.value || landscape.value};
  let g = layoutGroup(groupId.value, opts);
  if (!isPhone.value || !g.cards.length) return g;
  if (landscape.value) {
    // On its side: a group of one or two fills the box a three-across group takes, so its type matches theirs (R61 N3)
    const wide = landscapeGroupTile(g.cards.length);
    return wide ? layoutGroup(groupId.value, {...opts, columns: wide.columns, tileW: wide.tileW, tileH: wide.tileH}) : g;
  }
  // Upright phone: two across (one column, as wide as two, for a group of one or two: the same scale and type as the
  // others, R59 L3), the tiles as tall as fills the band in one screen
  const tile = phoneGroupTile(g.cards.length);
  if (tile.columns !== g.columns || tile.tileW !== g.cards[0].w) { opts.columns = tile.columns; opts.tileW = tile.tileW; g = layoutGroup(groupId.value, opts); }
  const rows = Math.ceil(g.cards.length / g.columns);
  const s = (vp.size.w - PAD.value * 2) / g.width;
  const tileH = phoneTileHeight({rows, availH: vp.size.h - inset.value.top - inset.value.bottom, pad: PAD.value, s, max: tile.maxH});
  return tileH === g.cards[0].h ? g : layoutGroup(groupId.value, {...opts, tileH});
});
// The tree wraps to the screen's width at the readable scale (so its text never has to shrink to fit)
const layoutWidth = computed(() => areaLayoutWidth({w: vp.size.w}, {pad: AREA_PAD.value, readable: readable.value}));
// An area's layout (the tree and its routed connectors) is kept once made, per width and checkbox state, and every
// area's is made ahead of time once the page is idle, so a search pick into an area has no layout to wait for (R53 M5)
const areaLayouts = new Map();
const areaLayout = (id, visible, maxWidth) => {
  const key = `${id}|${maxWidth}|${visible.size}`;
  let made = areaLayouts.get(key);
  if (!made) {
    made = layoutArea(id, {visible, maxWidth});
    areaLayouts.set(key, made);
  }
  return made;
};
// The inset an area's first screen is fitted under: the measured one inside an area; elsewhere (choosing a layout
// ahead of entering) the desktop bar's last in-area height, or its usual 17px line row on top of the bar
const areaInset = computed(() => {
  if (inArea.value || isPhone.value) return inset.value;
  const bar = areaBarH.value || (barH.value ? barH.value + 17 : 85);
  return {top: bar + 4, bottom: 0};
});
const areaEntryOpts = (ins) => ({pad: AREA_PAD.value, inset: ins, readable: readable.value, floor: entryFloor.value, hardFloor: entryHardFloor.value, ceiling: entryCeiling.value});
const memoLayout = (id, {visible, maxWidth}) => areaLayout(id, visible, maxWidth);
// The tree is laid out to the screen's readable width, or a little narrower when that is what lets its first screen
// end in a gap between rows rather than through a card (R56 M1: areaEntryLayout)
const pickArea = (id, visible) => areaEntryLayout(id, {visible, maxWidth: layoutWidth.value, viewport: {w: vp.size.w, h: vp.size.h}, entry: areaEntryOpts(areaInset.value), layout: memoLayout}).area;
const area = computed(() => (areaId.value ? pickArea(areaId.value, visibleIds.value) : null));
const prewarmLayouts = () => {
  const run = () => {
    const visible = visibleIds.value;
    for (const sct of overviewSections(visible)) pickArea(sct.id, visible);
  };
  try {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, {timeout: 2000});
    else setTimeout(run, 600);
  } catch (err) { /* no idle callback */ }
};
const groupData = computed(() => findSystemMapGroup(groupId.value));
const groupTone = computed(() => sectionTone(groupData.value));
const areaSection = computed(() => findSystemMapSection(areaId.value));
const areaTone = computed(() => sectionTone(areaSection.value));
// The canvas takes the tint of the level you are in
const levelTone = computed(() => (inArea.value ? areaTone.value : inGroup.value ? groupTone.value : sectionTone(null)));
const shownCount = computed(() => visibleIds.value.size);
const shownAreas = computed(() => overviewSections(visibleIds.value).length);
const groupCards = computed(() => all.value.cards.map((c) => {
  const g = findSystemMapGroup(c.id);
  return {...c, section: g, tone: sectionTone(g)};
}));
const areaCards = computed(() => (group.value ? group.value.cards : []).map((c) => {
  const section = findSystemMapSection(c.id);
  return {...c, section, tone: sectionTone(section)};
}));
// The Areas menu: every visible area under its group, the same grouping as the map
const areaMenu = computed(() => {
  const shown = new Set(overviewSections(visibleIds.value).map((s) => s.id));
  return SYSTEM_MAP_GROUPS.map((g) => ({
    id: g.id,
    name: g.name,
    tone: sectionTone(g),
    items: g.areas.filter((a) => shown.has(a)).map((a) => {
      const section = findSystemMapSection(a);
      return {id: a, name: section.name, art: section.art, count: SYSTEM_MAP_NODES.filter((n) => n.section === a && visibleIds.value.has(n.id)).length, tone: sectionTone(section)};
    }),
  })).filter((g) => g.items.length);
});

const holdsEdges = computed(() => (area.value ? area.value.holds : []));
const stepEdges = computed(() => (area.value ? area.value.steps : []));
// Same-area links are routed around the other cards by the layout, like the steps
const innerLinkEdges = computed(() => (area.value ? area.value.links : []));
const partsShown = computed(() => (area.value ? [...area.value.nodes.values()] : []));

// The server renders the all level fitted for a typical desktop (--sm-tf-ssr),
// and a small inline script re-fits it to the real canvas BEFORE the first
// paint (the canvas carries the grid's size and the fit's padding as data
// attributes; the script writes the transform and the type scale as CSS
// variables on the canvas, which the layer and the cards read until the page
// has mounted). So a fresh load never shows a clipped grid that then snaps
// (R48 L3). On mount the browser fits again to the same numbers, clears the
// script's variables, and from then on refits whenever the canvas resizes while
// the camera is at rest (M3).
// The script sits in the markup right after the map's bar (not at the end of the body: the parser painted the canvas
// once at the server's scale before reaching it, R59 L1), and until it has run the all layer is not drawn at all. Where
// a page sizes the map's box itself first (/help: data-sm-sizer on the box), the page's sizer runs the fit once it has.
// A shared link to a group, an area or a part (#payments, ?node=entry-fees) is read by the same script: the all level
// is then never drawn, the canvas takes the target's tint, and the map opens straight at the target on mount, with no
// cross-fade from the all level (R59 M1).
// SSR_TOP is the bar's measured 68px plus the 4px inset the map fits under (the pre-paint script measures the bar's
// real height when it can, so its fit is the one the map makes on mount and the first painted frame is final: R56 M3)
const SSR_PAD = 28, SSR_TOP = 72;
const ssrAll = layoutAll({columns: ALL_COLUMNS});
const ssrView = overviewEntryView(ssrAll, {w: 1440, h: 800}, {pad: SSR_PAD, inset: {top: SSR_TOP, bottom: 0}});
const ssrTransform = `translate3d(${ssrView.x}px, ${ssrView.y}px, 0) scale(${ssrView.s})`;
const fitData = {'data-fit-w': ssrAll.width, 'data-fit-h': ssrAll.height, 'data-fit-pad': SSR_PAD, 'data-fit-top': SSR_TOP};
// The pre-paint script (utils/systemMapLayout.js prefitScript), inline in the markup after the bar
const prefitHtml = `<script>${prefitScript()}<\/script>`;
/** The all level comes back into the page's own hands once a deep link has been opened (or turned out to name nothing that shows). */
const clearDeep = () => {
  const el = canvasEl.value?.closest?.('.sm');
  if (!el) return;
  // The bar's pre-paint words and the remembered box's hiding (R61 N4) go too: the page renders both itself by now
  // (data-sm-pre stays: it only says the script ran, and the bar's count is the page's own from here, R62 P2)
  for (const a of ['data-sm-deep', 'data-sm-crumbs', 'data-sm-crumb-area', 'data-sm-crumb-line', 'data-sm-admin-off']) el.removeAttribute(a);
  for (const t of el.querySelectorAll('.sm-canvas, .sm-bar')) {
    for (const v of ['--sm-deep-tint', '--sm-group', '--sm-current', '--sm-count', '--sm-ink', '--sm-line']) t.style.removeProperty(v);
  }
};
// A card level's type grows on a short canvas so a card's count stays readable (L7)
const cardFit = ref(ssrView.fitScale);
// A phone on its side fits its compact tiles small enough that a count could fall under 11px: it takes the boost too (R56 L3)
const typeK = computed(() => overviewTypeScale(cardFit.value, {compact: isPhone.value && !landscape.value, linePx: isPhone.value ? AREA_COUNT_PX_COMPACT : AREA_COUNT_PX}));
const nameK = computed(() => overviewNameScale(typeK.value));
/** Once the browser has fitted the map itself, the inline script's pre-paint fit is cleared and never applied again. */
const clearPrefit = () => {
  const el = canvasEl.value;
  if (!el) return;
  el.setAttribute('data-mounted', '1');
  for (const v of ['--sm-tf', '--area-type-k', '--area-name-k']) el.style.removeProperty(v);
};

const allBox = computed(() => ({x: 0, y: 0, w: all.value.width, h: all.value.height}));
const groupBox = computed(() => (group.value ? {x: 0, y: 0, w: group.value.width, h: group.value.height} : null));
const areaBox = computed(() => (area.value ? {x: 0, y: 0, w: area.value.width, h: area.value.height} : null));
const boxFor = (lvl) => (lvl === LEVELS.AREA ? areaBox.value : lvl === LEVELS.GROUP ? groupBox.value : allBox.value);

// The all level fills a phone's width when it is two or more across (one tall column fits whole instead); a group's cards
// are capped at the size the all level's cards show, so a two-card group reads like a six-card one (R53 L5)
const allEntry = () => overviewEntryView(all.value, vp.size, {pad: PAD.value, inset: inset.value, fillWidth: isPhone.value && !landscape.value && all.value.columns > 1});
const groupEntry = () => overviewEntryView(group.value, vp.size, {pad: PAD.value, inset: inset.value, fillWidth: isPhone.value && !landscape.value, maxScale: isPhone.value ? 1 : allEntry().fitScale});
const areaEntry = () => areaEntryView(area.value, vp.size, areaEntryOpts(inset.value));
/** Where the camera starts at a level (the level's layout must exist). The bars are measured first, so the view is fitted under their real height. */
const entryFor = (lvl) => {
  measureBars();
  return lvl === LEVELS.AREA ? areaEntry() : lvl === LEVELS.GROUP ? groupEntry() : allEntry();
};

const applyLimits = () => {
  const lvl = level.value;
  if (lvl === LEVELS.AREA && !area.value) return;
  if (lvl === LEVELS.GROUP && !group.value) return;
  const entry = entryFor(lvl);
  if (lvl !== LEVELS.AREA) cardFit.value = entry.fitScale;
  vp.setLimits({level: lvl, range: scaleRange(lvl, entry), bounds: boxFor(lvl), inset: inset.value});
};

// Where the camera was last put to rest (an entry view). While it is still
// there, a resize refits; once the user has zoomed or panned, a resize only
// clamps so their place is kept.
let restView = null;
const restAt = (view) => { restView = {x: view.x, y: view.y, s: view.s}; };
const atRest = () => sameView(vp.current(), restView);

/** Back to this level's starting view (the Fit button, the 0 key). */
const fitLevel = (animate = true) => {
  applyLimits();
  const lvl = level.value;
  if ((lvl === LEVELS.AREA && !area.value) || (lvl === LEVELS.GROUP && !group.value)) return;
  const entry = entryFor(lvl);
  restAt(entry);
  // Already there (a refit after the bar's measure that changed nothing): no fly, so the tray is not held back for one
  vp.setView(entry, {animate: animate && !sameView(vp.current(), entry)});
};

// ── Level transitions ─────────────────────────────────────────────────────
// Going deeper is a cross-fade (R53 M2): the layer you leave eases a little
// towards the card you clicked (RECEDE) and fades out quickly, while the
// entered layer starts transparent (`entering`) and fades in as the camera
// flies it in from slightly small, so no old words show through the gaps
// between the new cards. Stepping out: the inner layer shrinks and fades by
// CSS while the outer one comes back from where it receded to its entry view.
// The ease is slight on purpose: blowing a layer up several times forced a huge
// re-raster during the fly-in, which showed as long tasks on a slow machine
// (R45 L3).
const RECEDE = 1.12;
const receded = reactive({all: false, group: false});
const hidden = reactive({all: false, group: false});
const leaving = reactive({group: false, area: false});
const entering = reactive({group: false, area: false});
/** The entered layer mounts transparent and fades in once the camera has placed it (two frames on, so the start is painted). */
const fadeIn = (lvl) => {
  entering[lvl] = true;
  requestAnimationFrame(() => requestAnimationFrame(() => { entering[lvl] = false; }));
};
const recedeViews = {all: null, group: null};
const timers = {hide: null, leave: null};
const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });
const motionMs = () => (vp.reducedMotion.value ? 0 : 480);
const scaledAround = (t, k) => ({s: t.s * k, x: vp.size.w / 2 - (vp.size.w / 2 - t.x) * k, y: vp.size.h / 2 - (vp.size.h / 2 - t.y) * k});
const cssTransform = (t) => `translate3d(${t.x}px, ${t.y}px, 0) scale(${t.s})`;
/** The view a card layer rests at behind what was entered: blown up a little on that card. */
const recedeOn = (card, s) => ({s, x: vp.size.w / 2 - (card.x + card.w / 2) * s, y: vp.size.h / 2 - (card.y + card.h / 2) * s});
/** Puts a card layer to rest behind the level below it, on the given card, and hides it once faded. */
const recedeLayer = (lvl, card, s) => {
  recedeViews[lvl] = recedeOn(card, s);
  const el = layerEl(lvl);
  if (el) el.style.transform = cssTransform(recedeViews[lvl]);
  receded[lvl] = true;
};
// The group layer held back while an all → area fly runs (it is hidden behind the area anyway): mounted once the fly has
// landed, at its receded view, or at once when something needs it sooner (stepping out)
const groupDeferred = ref(false);
let groupMountTimer = null;
const deferGroupLayer = () => { clearTimeout(groupMountTimer); groupDeferred.value = true; };
const mountGroupLayer = async () => {
  clearTimeout(groupMountTimer);
  if (!groupDeferred.value) return;
  groupDeferred.value = false;
  await nextTick();
  const el = layerEl(LEVELS.GROUP);
  if (el && recedeViews.group && receded.group) el.style.transform = cssTransform(recedeViews.group);
};
const mountGroupLayerLater = (ms) => {
  clearTimeout(groupMountTimer);
  groupMountTimer = setTimeout(mountGroupLayer, ms + 80);
};
const hideReceded = () => {
  clearTimeout(timers.hide);
  timers.hide = setTimeout(() => {
    if (receded.all) hidden.all = true;
    if (receded.group) hidden.group = true;
  }, motionMs() + 40);
};
/**
 * The camera flies the entered level in from slightly small to its entry view,
 * or straight to `toPart`'s focus (a search pick, a chip, a deep link to a
 * part): one fly, not the entry's and then the part's, so the tray shows as
 * soon as it lands (R56 L4). Fit still goes back to the entry view.
 */
const flyIn = (lvl, animate, {toPart = null} = {}) => {
  const entry = entryFor(lvl);
  if (lvl !== LEVELS.AREA) cardFit.value = entry.fitScale;
  vp.setLimits({level: lvl, range: scaleRange(lvl, entry), bounds: boxFor(lvl), inset: inset.value});
  restAt(entry);
  const target = (toPart && focusView(toPart, entry.s)) || entry;
  if (animate && !vp.reducedMotion.value) {
    vp.setView(scaledAround(target, 0.86), {animate: false, clamp: false});
    vp.setView(target, {animate: true});
  } else {
    vp.setView(target, {animate: false});
  }
};

// History: going deeper pushes one entry per level so Back steps out one level
// at a time; switching, selecting, closing and stepping out replace. The flags
// remember whether this visit pushed an entry for each level, so the page's own
// controls can pop it (Back then does the stepping out, through the route
// watcher) instead of adding to the history.
let pushedGroup = false;
let pushedArea = false;
let ownNavs = 0;
// A Back the page asks the browser for settles when the route watcher has applied it: the promise the page's controls
// return resolves then (or after a grace period if the pop never arrives), so the gate stays shut meanwhile (R53 H1)
let popWaiter = null;
const awaitPop = () => new Promise((resolve) => {
  const done = () => { if (popWaiter === done) popWaiter = null; resolve(); };
  popWaiter = done;
  setTimeout(done, 1500);
});
/**
 * True when the history entry below this one is exactly where stepping out
 * lands (`to`: the group, or the all level), so popping it steps out one level
 * and never leaves the map nor lands somewhere else. The flags say the page
 * pushed an entry; the router's state confirms it is that one. A hash typed by
 * hand pushes its own entry (from Ladders, `#tv`: the entry below is Ladders),
 * so then the step out replaces instead (R56 L2: Esc went back to Ladders, not
 * out to Showing it off).
 */
const canPop = (to) => {
  try {
    const back = window.history.state?.back;
    if (typeof back !== 'string') return false;
    return back === router.resolve(routeTarget(to)).fullPath;
  } catch (err) { return false; }
};

/**
 * Fly into a group from the all level (or show another group at the group
 * level). `history: false` when the route already says so (Back, Forward, a
 * deep link).
 */
const enterGroup = async (id, {animate = true, history = true} = {}) => {
  if (!findSystemMapGroup(id) || !all.value.byId.has(id)) return;
  if (inArea.value) return;
  if (inGroup.value) {
    if (groupId.value !== id) await showGroup(id, {animate});
    if (history) writeUrl(); else writtenState = urlState();
    return;
  }
  clearTimeout(timers.leave);
  leaving.group = false;
  hoverCard.value = null;
  const cur = vp.current();
  const card = all.value.byId.get(id);
  if (animate && !vp.reducedMotion.value) entering.group = true;
  groupId.value = id;
  level.value = LEVELS.GROUP;
  await nextTick();
  recedeLayer(LEVELS.ALL, card, cur.s * RECEDE);
  hideReceded();
  flyIn(LEVELS.GROUP, animate);
  if (entering.group) fadeIn(LEVELS.GROUP);
  // No card lights up under a pointer that has not moved since the fly-in (R53 L4)
  if (!isPhone.value) disarmHover();
  if (history) writeUrl();
  else { writtenState = urlState(); pushedGroup = true; }
};

/** At the group level: fade this group's cards out and the other group's in, at that group's fit. */
const showGroup = async (id, {animate = true} = {}) => {
  if (!findSystemMapGroup(id)) return;
  hoverCard.value = null;
  if (animate && !vp.reducedMotion.value) {
    leaving.group = true;
    await wait(motionMs() / 2);
    leaving.group = false;
  }
  groupId.value = id;
  await nextTick();
  // The all layer rests on the new group's card from now on
  const card = all.value.byId.get(id);
  if (card) recedeLayer(LEVELS.ALL, card, recedeViews.all?.s || allEntry().s * RECEDE);
  fitLevel(false);
};

/**
 * Fly into an area. From the all level it passes through the area's group
 * (both layers recede). `select` highlights a part once inside, `open` opens
 * its dialog as well. Entering the area you are already in only moves to the
 * part. `history: false` when the route already says so (Back, Forward, deep
 * link).
 */
const enterArea = async (id, {animate = true, select = null, open = false, pulse = true, history = true} = {}) => {
  const section = findSystemMapSection(id);
  const g = systemMapGroupOf(id);
  if (!section || !g || !overviewSections(visibleIds.value).some((s) => s.id === id)) return;
  clearTimeout(timers.leave);
  leaving.area = false;
  leaving.group = false;
  const alreadyIn = inArea.value && areaId.value === id;
  const fromLevel = level.value;
  if (!alreadyIn) {
    hoverCard.value = null;
    clearHover();
    selectedId.value = null;
    const cur = vp.current();
    if (fromLevel === LEVELS.AREA && animate && !vp.reducedMotion.value) {
      // Fade the old area's parts out before they are swapped for the new ones
      leaving.area = true;
      await wait(motionMs() / 2);
      leaving.area = false;
    }
    // From a card level the parts mount transparent and fade in over the fading cards (an area switch has just faded its own parts out)
    if (fromLevel !== LEVELS.AREA && animate && !vp.reducedMotion.value) entering.area = true;
    // Straight from the all level the group layer is never shown on the way down: it mounts once the fly has landed,
    // so the pick's one frame of work is the area alone (R56 L4)
    if (fromLevel === LEVELS.ALL) deferGroupLayer();
    groupId.value = g.id;
    areaId.value = id;
    level.value = LEVELS.AREA;
    await nextTick();
    // Both card layers rest behind the area: the all layer on the group's card, the group layer on the area's card
    // (the one being left eases from where the camera is; the others keep their blow-up, or take their entry's)
    const groupCard = all.value.byId.get(g.id);
    const areaCard = group.value?.byId.get(id);
    if (groupCard) recedeLayer(LEVELS.ALL, groupCard, fromLevel === LEVELS.ALL ? cur.s * RECEDE : (recedeViews.all?.s || allEntry().s * RECEDE));
    if (areaCard) recedeLayer(LEVELS.GROUP, areaCard, fromLevel === LEVELS.GROUP ? cur.s * RECEDE : (recedeViews.group?.s || groupEntry().s * RECEDE));
    if (fromLevel === LEVELS.ALL) hidden.group = true; // never shown on the way down
    hideReceded();
    const toPart = select && area.value?.nodes.has(select) ? select : null;
    flyIn(LEVELS.AREA, animate, {toPart});
    if (entering.area) fadeIn(LEVELS.AREA);
    // No card lights up under a pointer that has not moved since the fly-in (L9)
    if (!isPhone.value) disarmHover();
    if (fromLevel === LEVELS.ALL) mountGroupLayerLater(animate ? motionMs() : 0);
  }
  if (select && area.value?.nodes.has(select)) {
    selectedId.value = select;
    if (alreadyIn) focusPart(select, {animate, pulse});
    else if (pulse) pulsePart(select);
    if (open) openId.value = select;
  } else if (open) {
    openId.value = null;
  }
  if (history) writeUrl();
  else {
    writtenState = urlState();
    if (!alreadyIn && fromLevel !== LEVELS.AREA) { pushedArea = true; if (fromLevel === LEVELS.ALL) pushedGroup = true; }
  }
  if (!openId.value) pushedDialog = false;
};

/** Steps out of the current layer to the one above it, by CSS and the camera. */
const ascend = async (fromLevel, toLevel, {animate = true} = {}) => {
  if (toLevel === LEVELS.GROUP) await mountGroupLayer();
  clearTimeout(timers.hide);
  if (toLevel !== LEVELS.ALL && receded.all) hidden.all = true; // the hide timer is cleared; the all layer stays out of the way
  hidden[toLevel] = false;
  const cur = vp.current();
  level.value = toLevel;
  const entry = entryFor(toLevel);
  if (toLevel !== LEVELS.AREA) cardFit.value = entry.fitScale;
  vp.setLimits({level: toLevel, range: scaleRange(toLevel, entry), bounds: boxFor(toLevel), inset: inset.value});
  restAt(entry);
  const ms = animate ? motionMs() : 0;
  const innerEl = layerEl(fromLevel);
  if (innerEl) {
    innerEl.style.transform = cssTransform(scaledAround(cur, 0.86));
    leaving[fromLevel] = true;
  }
  await nextTick();
  receded[toLevel] = false;
  if (ms && recedeViews[toLevel]) {
    vp.setView(recedeViews[toLevel], {animate: false, clamp: false});
    vp.setView(entry, {animate: true});
  } else {
    vp.setView(entry, {animate: false});
  }
  clearTimeout(timers.leave);
  timers.leave = setTimeout(() => {
    leaving[fromLevel] = false;
    if (!inArea.value) areaId.value = null;
    if (atAll.value) groupId.value = null;
  }, ms + 40);
  await wait(ms);
};

/**
 * Out of the area to its group. By the page's own controls this pops the entry
 * the enter pushed (and settles when the pop has been applied). `toGroup`: the
 * group to land on when it is not the one the group layer holds (Back after a
 * switch into another group's area): the layer takes that group's cards before
 * it is shown, so the wrong group never flashes first (R53 M3).
 */
const leaveArea = async ({animate = true, history = true, toGroup = null} = {}) => {
  if (!inArea.value) return;
  if (history && pushedArea && mounted.value && canPop({groupId: toGroup || groupId.value, areaId: null, selectedId: null, openId: null})) {
    // Back does the leaving: the route watcher calls us again with history: false
    pushedArea = false;
    const settled = awaitPop();
    router.back();
    return settled;
  }
  clearHover();
  selectedId.value = null;
  openId.value = null;
  pushedDialog = false;
  pushedArea = false;
  if (toGroup && toGroup !== groupId.value && findSystemMapGroup(toGroup)) {
    groupId.value = toGroup;
    recedeViews.group = null; // the layer appears at the new group's entry view (a fade-in, no glide from a stale card)
    await nextTick();
  }
  await ascend(LEVELS.AREA, LEVELS.GROUP, {animate});
  if (history) writeUrl();
  else writtenState = urlState();
};

/** Out of the group to the all level. By the page's own controls this pops the entry the enter pushed. */
const leaveGroup = async ({animate = true, history = true} = {}) => {
  if (!inGroup.value) return;
  if (history && pushedGroup && mounted.value && canPop({groupId: null, areaId: null, selectedId: null, openId: null})) {
    pushedGroup = false;
    const settled = awaitPop();
    router.back();
    return settled;
  }
  hoverCard.value = null;
  pushedGroup = false;
  await ascend(LEVELS.GROUP, LEVELS.ALL, {animate});
  if (history) writeUrl();
  else writtenState = urlState();
};

/**
 * One level out, whatever the level (the Back arrow, Backspace, Esc, the phone pill): one at a time, through the gate;
 * a second press while the first is still landing is kept (one at most) and steps out once it has (R56 L1).
 */
const stepOutNow = () => gated(() => (inArea.value ? leaveArea() : inGroup.value ? leaveGroup() : null));
const stepOut = () => { gate.later(stepOutNow); };

// ── Hover, selection, pulse (parts) and hover (cards) ─────────────────────
const hoverCard = ref(null);
const hoverId = ref(null);
const selectedId = ref(null);
const pulseId = ref(null);
let pulseTimer = null;
let hoverTimer = null;
let trayHovered = false;
// The corridor: after leaving a card the pointer may cross other cards on its
// way to that card's tray (the card, the tray and the rectangle between them)
// without the tray switching to them
let travel = null;      // {corridor, until}
let pendingHover = null;
const clearHover = () => {
  clearTimeout(hoverTimer);
  hoverId.value = null;
  travel = null;
  pendingHover = null;
};
// Hover is disarmed on entering an area until the pointer moves (L9); the CSS
// hover lift is gated by the same flag
const hoverArmed = ref(true);
let armAt = null;
const disarmHover = () => { hoverArmed.value = false; armAt = {x: pointer.x, y: pointer.y}; clearHover(); };
const armHover = () => {
  hoverArmed.value = true;
  armAt = null;
  // The card already under the pointer takes the hover now (no mouseenter will fire for it): a part inside an area, a card at a card level
  try {
    const r = canvasEl.value?.getBoundingClientRect();
    const el = r ? document.elementFromPoint(r.left + pointer.x, r.top + pointer.y) : null;
    const id = el?.closest?.('[data-node]')?.dataset?.node;
    if (id && area.value?.nodes.has(id)) setHover(id);
    const card = el?.closest?.('[data-area],[data-group]');
    if (card && !inArea.value) hoverCard.value = card.dataset.area || card.dataset.group || null;
  } catch (err) { /* no DOM */ }
};
/** A card at a card level under the pointer (null on leaving it); nothing until the pointer has moved after a fly-in. */
const setHoverCard = (id) => {
  if (id && !hoverArmed.value) return;
  hoverCard.value = id;
};
const inTravel = () => travel && Date.now() < travel.until && pointInCorridor(pointer, travel.corridor);
const checkTravel = () => {
  if (!travel) return;
  if (inTravel()) return;
  travel = null;
  if (pendingHover) { hoverId.value = pendingHover; pendingHover = null; clearTimeout(hoverTimer); }
};
// The travel ends when the pointer leaves the corridor or its time runs out: then the card the pointer is resting on
// (if any) takes the hover, whether or not it moves again (R51 L1: a still pointer on a crossed card never took it)
const travelTick = () => {
  if (trayHovered) return;
  if (inTravel()) { hoverTimer = setTimeout(travelTick, Math.min(120, Math.max(20, travel.until - Date.now() + 10))); return; }
  travel = null;
  hoverId.value = pendingHover || null;
  pendingHover = null;
};
const setHover = (id) => {
  if (id) {
    if (!hoverArmed.value) return;
    clearTimeout(hoverTimer);
    if (travel && id !== hoverId.value && inTravel()) {
      // Crossing another card on the way to the tray: it waits, and takes the hover if the pointer rests on it
      pendingHover = id;
      hoverTimer = setTimeout(travelTick, 120);
      return;
    }
    travel = null;
    pendingHover = null;
    hoverId.value = id;
    return;
  }
  pendingHover = null;
  const tray = trayPlacement.value;
  if (tray && !isPhone.value) travel = {corridor: trayCorridor(tray.anchor, tray), until: Date.now() + 1500};
  // Give the pointer time to reach the chips beside the card
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(travelTick, 160);
};
const onTrayEnter = () => { trayHovered = true; travel = null; pendingHover = null; clearTimeout(hoverTimer); };
// Off the tray back onto the canvas: the usual hand-back (the pointer may be on its way to the card); anywhere else (the
// page's header, the bar) has left the map, so the tray goes at once like any other way out of the canvas (R56 L6)
const onTrayLeave = (e) => {
  trayHovered = false;
  const to = e?.relatedTarget;
  if (to && canvasEl.value?.contains?.(to)) { setHover(null); return; }
  clearHover();
};
/** The pointer has left the canvas (the page's header, the bar): nothing stays hovered, unless it went onto the tray. */
const onCanvasLeave = () => {
  if (isPhone.value) return;
  travel = null;
  pendingHover = null;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => { if (!trayHovered) hoverId.value = null; }, 180);
};
const activeId = computed(() => (can.value.hoverParts ? hoverId.value || selectedId.value : null));
// Inside a group, hovering an area card lights its connectors and the related cards
const relatedAreas = computed(() => {
  if (!hoverCard.value || !group.value) return new Set();
  const out = new Set();
  for (const r of group.value.relations) {
    if (r.a === hoverCard.value) out.add(r.b);
    else if (r.b === hoverCard.value) out.add(r.a);
  }
  return out;
});
const relationClass = (r) => {
  if (!hoverCard.value) return '';
  return r.a === hoverCard.value || r.b === hoverCard.value ? 'is-hot' : 'is-faint';
};

/**
 * The view a part of the current area is shown at. From the entry's scale (a pick, a chip, a deep link, or a camera
 * not zoomed in past it): the area's rows and columns lined up so the canvas's edges fall between cards, the part
 * whole and as central as that allows (R59 L4: centring it cut its neighbours at every edge). A camera the user has
 * zoomed in keeps its zoom and centres the part.
 */
const focusView = (id, baseScale) => {
  const pos = area.value?.nodes.get(id);
  if (!pos) return null;
  const entry = areaEntry();
  if (baseScale <= entry.s * 1.05 + 1e-9) return partFocusView(area.value, id, vp.size, {inset: inset.value, entry, pad: AREA_PAD.value});
  const s = baseScale;
  const cx = pos.x + pos.w / 2, cy = pos.y + pos.h / 2;
  const avail = {top: inset.value.top, h: vp.size.h - inset.value.top - inset.value.bottom};
  return {s, x: vp.size.w / 2 - cx * s, y: avail.top + avail.h / 2 - cy * s};
};
const pulsePart = (id) => {
  clearTimeout(pulseTimer);
  pulseId.value = null;
  nextTick(() => {
    pulseId.value = id;
    pulseTimer = setTimeout(() => { pulseId.value = null; }, 3000);
  });
};
/** Move the camera to a part inside the current area and select it. */
const focusPart = (id, {animate = true, pulse = true} = {}) => {
  const view = focusView(id, vp.current().s);
  if (!view) return;
  vp.setView(view, {animate, ms: 420});
  selectedId.value = id;
  if (pulse) pulsePart(id);
};

/** Go to any part: enter its area if needed (through its group), select it, optionally open it. */
const goTo = (id, {open = false} = {}) => {
  const node = findSystemMapNode(id);
  if (!node || !visibleIds.value.has(id)) return;
  return enterArea(node.section, {select: id, open});
};

// ── Chips beside the active part: placed in free screen space, never over
// another card and, when away from its card, never against another card
// either. One column when it has room, a wider grid when it has not (beside a
// root with its whole tree underneath). A far tray is named by its caption and
// joined by a dotted leader when the line between them is clear canvas; the
// pointer reaches it along the corridor (the convex hull of card and tray, so
// any straight move to any chip stays inside). A phone docks them above its
// bar. ──
const trayEl = ref(null);
const trayLinks = computed(() => (activeId.value && can.value.chips ? crossAreaLinks(activeId.value, {within: visibleIds.value}) : []));
// The tray is re-placed in the current screen geometry on every move of the camera (a pan included), and shows fewer
// chips (the rest behind "+N more") when the room left beside the card is too small for all of them (R56 M2)
const trayCount = computed(() => trayPlacement.value?.shown ?? TRAY_MAX);
const trayShown = computed(() => trayLinks.value.slice(0, trayCount.value));
const trayMore = computed(() => Math.max(0, trayLinks.value.length - trayCount.value));
const trayPlacement = computed(() => {
  if (isPhone.value) return null;
  // Not while the camera flies: placing the tray on every frame of a fly was the jank on a search pick (R53 M5); it appears when the camera lands
  if (vp.animating.value) return null;
  const pos = activeId.value ? area.value?.nodes.get(activeId.value) : null;
  if (!pos || !trayLinks.value.length) return null;
  const view = {x: vp.view.x, y: vp.view.y, s: vp.view.s};
  const anchor = toScreenBox(pos, view);
  const obstacles = [];
  for (const other of partsShown.value) {
    if (other.id === pos.id) continue;
    const r = toScreenBox(other, view);
    if (r.x + r.w < 0 || r.y + r.h < 0 || r.x > vp.size.w || r.y > vp.size.h) continue;
    obstacles.push(r);
  }
  // The zoom controls, bottom right
  obstacles.push({x: vp.size.w - 14 - 36, y: vp.size.h - 14 - 120, w: 36, h: 120});
  const bounds = trayBounds(vp.size, inset.value);
  const placed = placeTrayFor({anchor, total: trayLinks.value.length, obstacles, bounds, max: TRAY_MAX});
  return placed ? {...placed, anchor, obstacles} : null;
});
const trayStyle = computed(() => {
  const t = trayPlacement.value;
  if (!t) return null;
  return {left: `${t.x}px`, top: `${t.y}px`, width: `${t.w}px`, '--tray-cols': t.cols, '--tray-cap': `${TRAY_CAP_H}px`};
});
// A tray away from its card is tethered by its caption (red) and, when the
// straight line between them crosses no other card, a dotted leader; never
// by anything drawn over other cards or their text
const trayFar = computed(() => !!trayPlacement.value && trayPlacement.value.distance > TRAY_FAR);
const trayLeader = computed(() => {
  if (!trayFar.value) return null;
  const line = leaderLine(trayPlacement.value.anchor, trayPlacement.value);
  return leaderIsClear(line, trayPlacement.value.obstacles) ? line : null;
});
const activeName = computed(() => findSystemMapNode(activeId.value)?.name || '');
// A phone has no hover: the selected part's chips dock above the bottom bar
const dockShown = computed(() => isPhone.value && !!activeId.value && trayLinks.value.length > 0);

// ── Search ────────────────────────────────────────────────────────────────
const query = ref('');
const searchEl = ref(null);
const resultIndex = ref(0);
// v-text-field's clear button sets the model to null, so read it through `q`
const q = computed(() => (query.value || '').trim());
const results = computed(() => searchSystemMap(q.value, {within: visibleIds.value}));
const searching = computed(() => q.value.length >= 2);
const resultSet = computed(() => (searching.value ? new Set(results.value.map((n) => n.id)) : null));
const matchesByArea = computed(() => {
  const out = new Map();
  if (!resultSet.value) return out;
  for (const n of results.value) out.set(n.section, (out.get(n.section) || 0) + 1);
  return out;
});
const matchesByGroup = computed(() => {
  const out = new Map();
  if (!resultSet.value) return out;
  for (const [sectionId, count] of matchesByArea.value) {
    const g = systemMapGroupOf(sectionId);
    if (g) out.set(g.id, (out.get(g.id) || 0) + count);
  }
  return out;
});
const hiddenMatches = computed(() => (searching.value ? searchSystemMap(q.value).length - results.value.length : 0));
watch(results, () => { resultIndex.value = 0; });
const partDim = (id) => !!resultSet.value && !resultSet.value.has(id);
const pick = (node) => {
  query.value = '';
  searchSheet.value = false;
  goTo(node.id);
};

// ── Dialog (acts in place; the open part is mirrored into the URL). Opening
// pushes a history entry so Back closes the dialog and leaves the area only on
// a second Back; the page's own close pops that entry, unless the dialog has
// moved to another part meanwhile (then the URL is simply replaced). ────────
const openId = ref(null);
const openNode = computed(() => findSystemMapNode(openId.value));
let pushedDialog = false;
let dialogMoved = false;
const closeDialog = () => {
  if (!openId.value) return;
  if (pushedDialog && !dialogMoved && mounted.value) {
    // Back does the closing: the route watcher applies it
    pushedDialog = false;
    router.back();
    return;
  }
  pushedDialog = false;
  openId.value = null;
  writeUrl();
};
const dialogOpen = computed({
  get: () => mounted.value && openNode.value != null,
  set: (v) => { if (!v) closeDialog(); },
});
const openConnections = computed(() => (openNode.value ? systemMapConnections(openNode.value.id).filter((c) => visibleIds.value.has(c.id)) : []));
const openCross = computed(() => (openNode.value ? crossAreaLinks(openNode.value.id, {within: visibleIds.value}) : []));
const openFlags = computed(() => (openNode.value ? flagsById.get(openNode.value.id) : {}));
const openPart = (id) => {
  if (!can.value.parts) return;
  selectedId.value = id;
  if (openId.value && openId.value !== id) dialogMoved = true;
  else if (!openId.value) dialogMoved = false;
  openId.value = id;
  writeUrl();
};
const clearSelection = () => {
  if (!selectedId.value) return;
  selectedId.value = null;
  writeUrl();
};
// From a chip beside a card: fly there and select it
const jumpFromTray = (id) => goTo(id);
// From inside the dialog: the dialog follows to the new part
const jumpFromDialog = (id) => { dialogMoved = true; return goTo(id, {open: true}); };
const sectionFromDialog = (id) => {
  openId.value = null;
  pushedDialog = false;
  enterArea(id);
};

// ── URL ↔ state. The route is the truth for Back/Forward and deep links:
// #group, #area or #part (selected) and ?node= (open). The page writes it
// through the router (a push per level going deeper, replaces otherwise), and
// a watcher applies whatever the route says the page does not already show.
// An element with the hash's id sits at the top of the page so the router's
// scroll-to-hash finds it (and scrolls nowhere) instead of warning. ──
const urlState = () => ({groupId: atAll.value ? null : groupId.value, areaId: inArea.value ? areaId.value : null, selectedId: selectedId.value, openId: openId.value});
let writtenState = {groupId: null, areaId: null, selectedId: null, openId: null};
const routeTarget = (state) => {
  const r = routeFor(state);
  const query = {...route.query};
  if (r.node) query.node = r.node;
  else delete query.node;
  return {path: route.path, query, hash: r.hash};
};
const notePushed = (state) => {
  if (state.openId) pushedDialog = true;
  else if (state.areaId) pushedArea = true;
  else if (state.groupId) pushedGroup = true;
};
const writeUrl = () => {
  if (!mounted.value || typeof window === 'undefined') return;
  const state = urlState();
  const target = routeTarget(state);
  if (router.resolve(target).fullPath === route.fullPath) { writtenState = state; return; }
  const action = historyAction(writtenState, state);
  const steps = historySteps(writtenState, state);
  // A switch into another group's area replaces the URL, so the entries below it (the old group, its area) no longer
  // match the breadcrumb: the page's own Back then steps out by replacing too, to the group the breadcrumb shows,
  // instead of popping back to the group you came in by (the browser's Back still does that)
  if (action === 'replace' && writtenState.groupId && state.groupId && writtenState.groupId !== state.groupId) { pushedGroup = false; pushedArea = false; }
  writtenState = state;
  if (action === 'none') return;
  ownNavs++;
  let nav;
  if (action === 'push') {
    // One entry per level passed through, so Back steps out one level at a time
    nav = steps.reduce((p, st) => p.then(() => { notePushed(st); return router.push(routeTarget(st)); }), Promise.resolve());
  } else {
    nav = router.replace(target);
  }
  Promise.resolve(nav).catch(() => {}).finally(() => { ownNavs = Math.max(0, ownNavs - 1); });
};
/** What the route asks for: {group, area, select, open} or nothing. */
const wanted = () => routeWants({hash: route.hash, node: typeof route.query.node === 'string' ? route.query.node : null}, visibleIds.value);
const anchorId = computed(() => (mounted.value ? hashAnchorId(route.hash) : null));
/** Applies the route to the map (initial load, Back, Forward, a pasted link). */
const syncFromRoute = async ({animate = true} = {}) => {
  const w = wanted();
  if (!w) {
    if (inArea.value) await leaveArea({animate: false, history: false});
    if (inGroup.value) await leaveGroup({animate, history: false});
    return;
  }
  if (!w.area) {
    // The group level (out of an area straight onto the group the route names: no other group shows first, R53 M3)
    if (inArea.value) await leaveArea({animate, history: false, toGroup: w.group});
    if (inGroup.value && groupId.value !== w.group) await showGroup(w.group, {animate});
    else if (atAll.value) await enterGroup(w.group, {animate, history: false});
    writtenState = urlState();
    return;
  }
  const sameArea = inArea.value && areaId.value === w.area;
  if (!sameArea) {
    await enterArea(w.area, {animate, select: w.select, open: w.open, pulse: !!w.select, history: false});
    return;
  }
  if ((w.select || null) !== (selectedId.value || null)) {
    if (w.select) focusPart(w.select, {animate, pulse: true});
    else selectedId.value = null;
  }
  if (w.open) {
    if (openId.value !== w.select) dialogMoved = !!openId.value;
    openId.value = w.select;
  } else if (openId.value) {
    openId.value = null;
    pushedDialog = false;
  }
  writtenState = urlState();
};
watch(() => route.fullPath, async () => {
  if (!ready.value || ownNavs > 0) return;
  try { await syncFromRoute(); } finally { popWaiter?.(); }
});

// ── Phone sheets, Areas menu ──────────────────────────────────────────────
const searchSheet = ref(false);
const areasSheet = ref(false);
const areasMenu = ref(false);
const phoneSearchEl = ref(null);
watch(searchSheet, (v) => { if (v) nextTick(() => phoneSearchEl.value?.focus?.()); });
const chooseArea = (id) => {
  areasSheet.value = false;
  areasMenu.value = false;
  enterArea(id);
};

// ── Keyboard ──────────────────────────────────────────────────────────────
const onKey = (e) => {
  // A keyboard user never moves the pointer: any key arms the hover (focus highlights) after a fly-in
  if (!hoverArmed.value) { hoverArmed.value = true; armAt = null; }
  const tag = e.target?.tagName;
  const typing = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
  if (e.key === '/' && !typing) {
    e.preventDefault();
    if (isPhone.value) searchSheet.value = true;
    else searchEl.value?.focus?.();
    return;
  }
  if (typing || dialogOpen.value || areasMenu.value || areasSheet.value || searchSheet.value) return;
  if (e.repeat && (e.key === 'Escape' || e.key === 'Backspace')) return; // a held key steps out once
  if (e.key === 'Escape') {
    if (q.value) query.value = '';
    else if (selectedId.value) clearSelection();
    else stepOut();
    return;
  }
  if (e.key === '+' || e.key === '=') { vp.zoomAt(1.25, undefined, undefined, true, {discrete: true}); return; }
  if (e.key === '-') { vp.zoomAt(0.8, undefined, undefined, true, {discrete: true}); return; }
  if (e.key === '0') { fitLevel(); return; }
  if (e.key === 'Backspace' && !atAll.value) { e.preventDefault(); stepOut(); return; }
  const step = 80;
  const v = vp.current();
  if (e.key === 'ArrowLeft') vp.setView({x: v.x + step, y: v.y, s: v.s});
  else if (e.key === 'ArrowRight') vp.setView({x: v.x - step, y: v.y, s: v.s});
  else if (e.key === 'ArrowUp') vp.setView({x: v.x, y: v.y + step, s: v.s});
  else if (e.key === 'ArrowDown') vp.setView({x: v.x, y: v.y - step, s: v.s});
};
const onSearchKey = (e) => {
  if (e.key === 'Escape') { query.value = ''; return; }
  if (!results.value.length) return;
  if (e.key === 'ArrowDown') { e.preventDefault(); resultIndex.value = (resultIndex.value + 1) % results.value.length; }
  else if (e.key === 'ArrowUp') { e.preventDefault(); resultIndex.value = (resultIndex.value - 1 + results.value.length) % results.value.length; }
  else if (e.key === 'Enter') { e.preventDefault(); pick(results.value[Math.min(resultIndex.value, results.value.length - 1)]); }
};

// Clicking empty canvas clears the selection
const onCanvasClick = (e) => {
  if (vp.dragging.value) return;
  if (e.target === canvasEl.value || e.target?.classList?.contains('sm-layer') || e.target?.closest?.('.sm-edges')) {
    clearSelection();
    clearHover();
  }
};

// ── Refits: the checkbox, the phone/desktop switch, the canvas size ───────
const ready = ref(false);
watch(showAdmin, async () => {
  if (!ready.value) return;
  remember();
  await nextTick();
  if (inArea.value && !overviewSections(visibleIds.value).some((s) => s.id === areaId.value)) { await leaveArea({animate: false}); return; }
  if (inGroup.value && !all.value.byId.has(groupId.value)) { await leaveGroup({animate: false}); return; }
  if (openId.value && !visibleIds.value.has(openId.value)) openId.value = null;
  if (selectedId.value && !visibleIds.value.has(selectedId.value)) selectedId.value = null;
  fitLevel(true);
});
watch(isPhone, async () => {
  if (!ready.value) return;
  await nextTick();
  observeBars();
  fitLevel(false);
});
let mountedAt = 0;
let pendingRefit = false;
const refit = () => {
  // Never mid-fly (the fly's target was fitted under the measured bars; cutting it short would leave the camera where it was): once it lands
  if (vp.animating.value) { pendingRefit = true; return; }
  // A camera still at its entry view refits (eased once the page has settled, so a late measure never snaps: R51 L3);
  // one the user has moved keeps its place within the new limits
  if (atRest()) { fitLevel(mounted.value && performance.now() - mountedAt > 300); return; }
  applyLimits();
  vp.setView(vp.current(), {animate: false});
};
watch(() => vp.animating.value, (flying) => {
  if (!flying && pendingRefit) { pendingRefit = false; refit(); }
});
watch(() => [vp.size.w, vp.size.h], () => {
  if (!mounted.value) return;
  // The canvas changed size (the page measured its height, a window resize, a rotation)
  refit();
});
// The bars' measured heights changed (a row for the area's line): what is fitted under them follows
watch(() => [inset.value.top, inset.value.bottom], () => {
  if (!mounted.value) return;
  refit();
});

let media = null;
const onMedia = (e) => { isPhone.value = e.matches; };
onMounted(async () => {
  restore();
  try {
    media = window.matchMedia('(max-width: 959px)');
    isPhone.value = media.matches;
    media.addEventListener('change', onMedia);
  } catch (err) { isPhone.value = false; }
  mounted.value = true;
  mountedAt = performance.now();
  vp.attach();
  await nextTick();
  observeBars();
  vp.measure();
  fitLevel(false);
  prewarmLayouts();
  // The camera's first transform lands in the next frame; the pre-paint fit goes once it has
  requestAnimationFrame(clearPrefit);
  // Deep links: ?node= opens a part inside its area; #group enters the group; #area enters the area; #part selects it.
  // A deep link is the page's own entry: stepping out replaces, never pops.
  await syncFromRoute({animate: false});
  // A deep link opened straight at its target, the all level never drawn (R59 M1): it stays out of sight (not faded
  // out, which would show it once at full strength as the pre-paint hiding ends) and the page takes over
  if (!atAll.value) hidden.all = true;
  await nextTick();
  clearDeep();
  pushedGroup = false;
  pushedArea = false;
  window.addEventListener('keydown', onKey);
  ready.value = true;
});
onBeforeUnmount(() => {
  clearTimeout(groupMountTimer);
  window.removeEventListener('keydown', onKey);
  media?.removeEventListener('change', onMedia);
  barsRo?.disconnect();
  clearTimeout(pulseTimer);
  clearTimeout(hoverTimer);
  clearTimeout(timers.leave);
  clearTimeout(timers.hide);
});

const fitWord = computed(() => (inArea.value ? 'Fit this area' : inGroup.value ? 'Fit this group' : 'Fit everything'));
const canvasLabel = computed(() => {
  if (inArea.value) return `${areaSection.value?.name}: its parts. Drag to pan, scroll or pinch to zoom, Escape to step out.`;
  if (inGroup.value) return `${groupData.value?.name}: its areas. Click an area to open it, Escape to step out.`;
  return 'Map of how Tap Leagues fits together: a card per group. Click a group to open it.';
});
</script>

<template>
  <div
      class="sm"
      :class="{'sm--embedded': embedded, 'sm--phone': isPhone, 'sm--area': inArea, 'sm--group': inGroup, 'sm--dragging': vp.dragging.value, 'sm--searching': searching, 'sm--hover-off': !hoverArmed, 'sm--ready': ready}"
      :style="{height: embedded ? '100dvh' : height, '--area-tint': levelTone.tint, '--area-ink': levelTone.ink, '--part-label-px': `${PART_LABEL_PX}px`, '--area-type-k': typeK, '--area-name-k': nameK}"
  >
    <!-- The router scrolls to the hash's element: this one sits at the very top, so it scrolls nowhere and never warns -->
    <span v-if="anchorId" :id="anchorId" class="sm-anchor" aria-hidden="true"/>
    <!-- ── Canvas (the data-fit attributes feed the pre-paint fit; its CSS variables live here until mount) ── -->
    <div
        ref="canvasEl"
        class="sm-canvas"
        role="application"
        :aria-label="canvasLabel"
        v-bind="fitData"
        @click="onCanvasClick"
        @pointerleave="onCanvasLeave"
    >
      <!-- All layer: one card per group. Its transform before the camera takes over is --sm-tf (the pre-paint fit) or the server's -->
      <div
          ref="allEl"
          class="sm-layer sm-layer--all"
          :class="{'sm-layer--passive': !atAll, 'sm-layer--receded': receded.all, 'sm-layer--hidden': hidden.all}"
          :style="{'--sm-tf-ssr': ssrTransform}"
      >
        <system-map-area
            v-for="c in groupCards"
            :key="c.id"
            :area="c"
            :section="c.section"
            :tone="c.tone"
            unit="areas"
            :interactive="can.groupCards"
            :compact="all.compact"
            :current="c.id === groupId"
            :dim="!!resultSet && !matchesByGroup.get(c.id)"
            :matches="matchesByGroup.get(c.id) || 0"
            @enter="(id) => !vp.dragging.value && enterGroup(id)"
            @hover="setHoverCard"
        />
      </div>

      <!-- Group layer: the entered group's areas, soft connectors between the related ones -->
      <div
          v-if="group && !groupDeferred"
          ref="groupEl"
          class="sm-layer sm-layer--group"
          :class="{'sm-layer--passive': !inGroup, 'sm-layer--receded': receded.group, 'sm-layer--hidden': hidden.group, 'sm-layer--leaving': leaving.group, 'sm-layer--entering': entering.group}"
      >
        <svg class="sm-edges" :width="group.width" :height="group.height" :viewBox="`0 0 ${group.width} ${group.height}`" aria-hidden="true">
          <path v-for="r in group.relations" :key="`${r.a}>${r.b}`" :d="r.d" class="smk smk-relation" :class="relationClass(r)"/>
        </svg>
        <system-map-area
            v-for="a in areaCards"
            :key="a.id"
            :area="a"
            :section="a.section"
            :tone="a.tone"
            unit="parts"
            :interactive="can.areaCards"
            :compact="group.compact"
            :current="a.id === areaId"
            :hot="relatedAreas.has(a.id)"
            :dim="!!resultSet && !matchesByArea.get(a.id)"
            :matches="matchesByArea.get(a.id) || 0"
            @enter="(id) => !vp.dragging.value && enterArea(id)"
            @hover="setHoverCard"
        />
      </div>

      <!-- Area layer: the entered area's parts -->
      <div
          v-if="area"
          ref="areaEl"
          class="sm-layer sm-layer--area"
          :class="{'sm-layer--passive': !inArea, 'sm-layer--leaving': leaving.area, 'sm-layer--entering': entering.area}"
      >
        <svg class="sm-edges" :width="area.width" :height="area.height" :viewBox="`0 0 ${area.width} ${area.height}`" aria-hidden="true">
          <g class="sm-holds">
            <path v-for="h in holdsEdges" :key="`${h.from}>${h.to}`" :d="h.d" class="smk smk-hold" :class="{'is-hot': activeId && (h.from === activeId || h.to === activeId)}"/>
          </g>
          <g class="sm-steps">
            <defs>
              <marker id="sm-step-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M1 1 L9 5 L1 9 Z" class="smk-arrow"/>
              </marker>
              <marker id="sm-step-arrow-hot" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M1 1 L9 5 L1 9 Z" class="smk-arrow smk-arrow--hot"/>
              </marker>
            </defs>
            <path
                v-for="st in stepEdges"
                :key="`s-${st.from}>${st.to}`"
                :d="st.d"
                class="smk smk-step"
                :class="{'is-hot': activeId && (st.from === activeId || st.to === activeId)}"
                :marker-end="activeId && (st.from === activeId || st.to === activeId) ? 'url(#sm-step-arrow-hot)' : 'url(#sm-step-arrow)'"
            />
          </g>
          <g class="sm-links">
            <path v-for="l in innerLinkEdges" :key="`${l.from}>${l.to}`" :d="l.d" class="smk smk-link" :class="{'is-hot': activeId && (l.from === activeId || l.to === activeId)}"/>
            <text v-for="l in innerLinkEdges" :key="`t-${l.from}>${l.to}`" :x="l.mid.x" :y="l.mid.y - 5" class="smk-label smk-label--link" text-anchor="middle">{{ l.label }}</text>
          </g>
        </svg>
        <system-map-node
            v-for="pos in partsShown"
            :key="pos.id"
            :node="findSystemMapNode(pos.id)"
            :pos="pos"
            :flags="flagsById.get(pos.id)"
            :dim="partDim(pos.id)"
            :active="pos.id === activeId"
            :open="pos.id === openId"
            :pulse="pos.id === pulseId"
            :interactive="can.parts"
            @open="(id) => !vp.dragging.value && openPart(id)"
            @hover="setHover"
        />
      </div>
    </div>

    <!-- ── Chips beside the active part: its links to other areas, in free space.
         A tray that had to sit away from its card is named by its caption and, through clear canvas only, a dotted leader ── -->
    <svg v-if="trayLeader" class="sm-leader" aria-hidden="true">
      <line :x1="trayLeader.x1" :y1="trayLeader.y1" :x2="trayLeader.x2" :y2="trayLeader.y2"/>
      <circle :cx="trayLeader.x1" :cy="trayLeader.y1" r="3"/>
    </svg>
    <transition name="sm-tray">
      <div
          v-if="trayStyle"
          ref="trayEl"
          class="sm-tray"
          :class="{'sm-tray--far': trayFar}"
          :style="trayStyle"
          role="group"
          :aria-label="`${activeName}: connected to other areas`"
          @mouseenter="onTrayEnter"
          @mouseleave="onTrayLeave"
      >
        <div class="sm-tray-cap"><strong>{{ activeName }}</strong> · connected to</div>
        <system-map-chip
            v-for="c in trayShown"
            :key="c.id"
            :link="c"
            size="sm"
            :from-name="activeName"
            @jump="jumpFromTray"
        />
        <button v-if="trayMore" type="button" class="sm-tray-more" @click.stop="openPart(activeId)">
          +{{ trayMore }} more<v-icon size="14">mdi-arrow-right</v-icon>
        </button>
      </div>
    </transition>

    <!-- ── Phone: the selected part's chips dock above the bar (no hover on a phone) ── -->
    <transition name="sm-tray">
      <div v-if="dockShown" class="sm-dock" role="group" :aria-label="`${activeName}: connected to other areas`">
        <div class="sm-dock-head">
          <span class="sm-dock-title"><strong>{{ activeName }}</strong> · connected to</span>
          <tl-icon-btn tooltip="Hide" icon="mdi-close" @click="clearSelection(); clearHover()"/>
        </div>
        <div class="sm-dock-row">
          <system-map-chip
              v-for="c in trayShown"
              :key="c.id"
              :link="c"
              size="md"
              :from-name="activeName"
              @jump="jumpFromTray"
          />
          <button v-if="trayMore" type="button" class="sm-tray-more" @click.stop="openPart(activeId)">
            +{{ trayMore }} more<v-icon size="14">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>
    </transition>

    <!-- ── Desktop bar: one row of where you are (All › group › area), search, Areas, the checkbox; inside an area the
         area's line reads on a second row under the crumbs, so the bar's tools never wrap (R53 M1) ── -->
    <div v-if="!isPhone" ref="barEl" class="sm-bar">
      <nav class="sm-crumbs" aria-label="Where you are">
        <div class="sm-crumb-row">
          <!-- A class on tl-icon-btn lands on its tooltip, so the margin sits on a wrapper round the button (R56 L7) -->
          <span v-if="!atAll" class="sm-back"><tl-icon-btn :tooltip="inArea ? `Back to ${groupData?.name}` : 'Back to all'" icon="mdi-arrow-left" @click="stepOut()"/></span>
          <button type="button" class="sm-crumb" :class="{'sm-crumb--current': atAll}" :disabled="atAll" @click="leaveGroup()">All</button>
          <template v-if="!atAll && groupData">
            <v-icon size="16" class="sm-crumb-sep">mdi-chevron-right</v-icon>
            <button v-if="inArea" type="button" class="sm-crumb" @click="leaveArea()">{{ groupData.name }}</button>
            <span v-else class="sm-crumb sm-crumb--current sm-crumb--area">
              <system-map-art :name="groupData.art" :size="18"/><span class="sm-crumb-text">{{ groupData.name }}</span>
            </span>
          </template>
          <template v-if="inArea && areaSection">
            <v-icon size="16" class="sm-crumb-sep">mdi-chevron-right</v-icon>
            <span class="sm-crumb sm-crumb--current sm-crumb--area">
              <system-map-art :name="areaSection.art" :size="18"/><span class="sm-crumb-text">{{ areaSection.name }}</span>
            </span>
          </template>
          <span v-else-if="inGroup && group" class="sm-crumb-count">{{ group.cards.length }} {{ group.cards.length === 1 ? 'area' : 'areas' }}</span>
          <span v-else-if="atAll" class="sm-crumb-count">{{ shownAreas }} areas · {{ shownCount }} parts</span>
        </div>
        <!-- A shared link's crumbs before the page takes over (R62 P1): a copy of the row above at an area (or a group),
             the Back arrow, separators and the icon's room included, its words written by the pre-paint script into
             the bar's custom properties. Drawn only once the script marks the root data-sm-crumbs; gone when ready. -->
        <div v-if="!ready" class="sm-crumb-row sm-crumb-row--pre" aria-hidden="true">
          <span class="sm-back"><tl-icon-btn tooltip="Back" icon="mdi-arrow-left"/></span>
          <button type="button" class="sm-crumb" tabindex="-1">All</button>
          <v-icon size="16" class="sm-crumb-sep">mdi-chevron-right</v-icon>
          <button type="button" class="sm-crumb sm-pre-group" tabindex="-1"/>
          <v-icon size="16" class="sm-crumb-sep sm-pre-group-sep">mdi-chevron-right</v-icon>
          <span class="sm-crumb sm-crumb--current sm-crumb--area sm-pre-current"><span class="sm-pre-art"/><span class="sm-crumb-text"/></span>
          <span class="sm-crumb-count sm-pre-count"/>
        </div>
        <span v-if="inArea && areaSection" class="sm-crumb-line">{{ areaSection.line }}</span>
      </nav>
      <div class="sm-tools">
        <div class="sm-search">
          <v-text-field
              id="system-map-search"
              ref="searchEl"
              v-model="query"
              density="compact"
              variant="outlined"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              placeholder="Search the map  ( / )"
              autocomplete="off"
              @keydown="onSearchKey"
          />
          <div v-if="searching" class="sm-results tl-card" role="listbox" aria-label="Search results">
            <button
                v-for="(n, i) in results.slice(0, 9)"
                :key="n.id"
                type="button"
                class="sm-result"
                :class="{'sm-result--active': i === resultIndex}"
                role="option"
                :aria-selected="i === resultIndex"
                @mouseenter="resultIndex = i"
                @click="pick(n)"
            >
              <system-map-art :name="n.art" :size="26"/>
              <span class="sm-result-text">
                <span class="sm-result-name">{{ n.name }}</span>
                <span class="sm-result-sec">{{ findSystemMapSection(n.section)?.name }} · {{ n.line }}</span>
              </span>
            </button>
            <div v-if="!results.length" class="sm-result-empty">Nothing on the map matches that.</div>
            <div v-if="hiddenMatches" class="sm-result-empty">{{ hiddenMatches }} more among the admin features.</div>
          </div>
        </div>
        <v-menu v-model="areasMenu" location="bottom end" :close-on-content-click="true" transition="fade-transition">
          <template #activator="{props: act}">
            <button v-bind="act" type="button" class="sm-areas-btn" :aria-expanded="areasMenu" aria-haspopup="menu">
              <v-icon size="18">mdi-view-grid-outline</v-icon>Areas<v-icon size="16">mdi-chevron-down</v-icon>
            </button>
          </template>
          <div class="sm-areas-menu tl-card" role="menu">
            <div v-for="g in areaMenu" :key="g.id" class="sm-areas-group" :style="{'--group-ink': g.tone.ink}">
              <div class="sm-areas-group-label">{{ g.name }}</div>
              <button
                  v-for="a in g.items"
                  :key="a.id"
                  type="button"
                  role="menuitemradio"
                  class="sm-areas-item"
                  :class="{'sm-areas-item--current': inArea && a.id === areaId}"
                  :aria-checked="inArea && a.id === areaId"
                  :style="{'--chip-tint': a.tone.tint, '--chip-ink': a.tone.ink}"
                  @click="chooseArea(a.id)"
              >
                <span class="sm-areas-art"><system-map-art :name="a.art" :size="18"/></span>
                <span class="sm-areas-name">{{ a.name }}</span>
                <span class="sm-areas-count">{{ a.count }}</span>
                <v-icon v-if="inArea && a.id === areaId" size="16" class="sm-areas-tick">mdi-check</v-icon>
              </button>
            </div>
          </div>
        </v-menu>
        <v-checkbox
            v-model="showAdmin"
            class="sm-check"
            label="Show admin features"
            density="compact"
            hide-details
            color="primary"
        />
      </div>
    </div>

    <!-- ── Phone: where you are (the pill steps out one level; inside an area it carries the area's line, up to two lines) ── -->
    <div v-if="isPhone" ref="phoneTopEl" class="sm-phone-top">
      <button v-if="!atAll" type="button" class="sm-phone-crumb" :class="{'sm-phone-crumb--two': inArea}" @click="stepOut()">
        <v-icon size="20">mdi-arrow-left</v-icon>
        <system-map-art v-if="inArea && areaSection" :name="areaSection.art" :size="18"/>
        <system-map-art v-else-if="groupData" :name="groupData.art" :size="18"/>
        <span class="sm-phone-crumb-text">
          <span class="sm-phone-crumb-name">{{ inArea ? areaSection?.name : groupData?.name }}</span>
          <span v-if="inArea && areaSection" class="sm-phone-crumb-line">{{ areaSection.line }}</span>
        </span>
      </button>
      <span v-else class="sm-phone-crumb sm-phone-crumb--static">All · {{ shownAreas }} areas</span>
    </div>

    <!-- ── Zoom controls (a phone pinches; its Fit is in the bar) ── -->
    <div v-if="!isPhone" class="sm-zoom">
      <tl-icon-btn tooltip="Zoom in" icon="mdi-plus" location="left" @click="vp.zoomAt(1.3, undefined, undefined, true, {discrete: true})"/>
      <tl-icon-btn tooltip="Zoom out" icon="mdi-minus" location="left" @click="vp.zoomAt(0.77, undefined, undefined, true, {discrete: true})"/>
      <tl-icon-btn :tooltip="fitWord" icon="mdi-fit-to-screen-outline" location="left" @click="fitLevel()"/>
    </div>

    <!-- The pre-paint fit and deep-link read (see prefitHtml): parsed and run right here on a fresh load, inert after -->
    <div class="sm-prefit" aria-hidden="true" v-html="prefitHtml"/>

    <!-- ── Phone bar ── -->
    <div v-if="isPhone" ref="bottomEl" class="sm-bottom">
      <button type="button" class="sm-bottom-btn" @click="searchSheet = true"><v-icon size="20">mdi-magnify</v-icon><span>Search</span></button>
      <button type="button" class="sm-bottom-btn sm-bottom-btn--wide" @click="areasSheet = true">
        <v-icon size="20">mdi-view-grid-outline</v-icon><span class="sm-bottom-current">Areas</span><v-icon size="16">mdi-chevron-up</v-icon>
      </button>
      <button type="button" class="sm-bottom-btn sm-bottom-btn--icon" :aria-label="fitWord" @click="fitLevel()">
        <v-icon size="22">mdi-fit-to-screen-outline</v-icon>
      </button>
    </div>

    <!-- ── Part dialog ── -->
    <system-map-dialog
        v-model="dialogOpen"
        :node="openNode"
        :flags="openFlags"
        :connections="openConnections"
        :cross-links="openCross"
        :visible="visibleIds"
        @jump="jumpFromDialog"
        @section="sectionFromDialog"
    />

    <!-- ── Phone sheets ── -->
    <tl-dialog v-model="searchSheet" title="Search the map" content-class="smd-sheet" :max-width="560">
      <v-text-field
          id="system-map-search-phone"
          ref="phoneSearchEl"
          v-model="query"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          autofocus
          prepend-inner-icon="mdi-magnify"
          placeholder="Players, bookings, entry fees…"
          autocomplete="off"
          @keydown="onSearchKey"
      />
      <div class="sm-sheet-list">
        <button v-for="n in results.slice(0, 12)" :key="n.id" type="button" class="sm-result" @click="pick(n)">
          <system-map-art :name="n.art" :size="26"/>
          <span class="sm-result-text">
            <span class="sm-result-name">{{ n.name }}</span>
            <span class="sm-result-sec">{{ findSystemMapSection(n.section)?.name }} · {{ n.line }}</span>
          </span>
        </button>
        <div v-if="searching && !results.length" class="sm-result-empty">Nothing on the map matches that.</div>
        <div v-if="hiddenMatches" class="sm-result-empty">{{ hiddenMatches }} more among the admin features.</div>
      </div>
    </tl-dialog>

    <tl-dialog v-model="areasSheet" title="Areas" content-class="smd-sheet" :max-width="560" flush>
      <div class="sm-sheet-areas">
        <template v-for="g in areaMenu" :key="g.id">
          <div class="sm-sheet-group-label" :style="{'--group-ink': g.tone.ink}">{{ g.name }}</div>
          <button
              v-for="a in g.items"
              :key="a.id"
              type="button"
              class="sm-sheet-area"
              :class="{'sm-sheet-area--current': inArea && a.id === areaId}"
              :style="{'--chip-tint': a.tone.tint, '--chip-ink': a.tone.ink}"
              @click="chooseArea(a.id)"
          >
            <span class="sm-sheet-area-art"><system-map-art :name="a.art" :size="22"/></span>
            <span class="sm-sheet-area-name">{{ a.name }}</span>
            <span class="sm-areas-count">{{ a.count }}</span>
            <v-icon v-if="inArea && a.id === areaId" size="18" class="sm-areas-tick">mdi-check</v-icon>
          </button>
        </template>
        <div class="sm-sheet-foot">
          <v-checkbox v-model="showAdmin" label="Show admin features" density="compact" hide-details color="primary"/>
          <span class="sm-crumb-count">{{ shownCount }} parts</span>
        </div>
      </div>
    </tl-dialog>
  </div>
</template>

<style scoped lang="scss">
.sm {
  position: relative;
  width: 100%;
  min-height: 260px;
  overflow: hidden;
  background: rgb(var(--v-theme-light));
  border-radius: 16px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  font-family: inherit;

  &--embedded {
    border-radius: 0;
    border: none;
    min-height: 100dvh;
  }
}

/* A phone on its side: a shorter map rather than a bar pushed off the screen (R48 L2) */
@media (max-height: 560px) {
  .sm:not(.sm--embedded) {
    min-height: 200px;
  }
}

.sm-canvas {
  position: absolute;
  inset: 0;
  overflow: hidden;
  touch-action: none;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  /* A faint grid gives the pan a sense of movement */
  background-image: radial-gradient(rgba(26, 26, 26, 0.07) 1px, transparent 1px);
  background-size: 28px 28px;
  transition: background-color 480ms ease;
}

/* Inside a group or an area the canvas takes its tint, softly */
.sm--group .sm-canvas,
.sm--area .sm-canvas {
  background-color: color-mix(in srgb, var(--area-tint) 55%, rgb(var(--v-theme-light)));
}

.sm--dragging .sm-canvas {
  cursor: grabbing;
}

/* A phone's pill sits 10px in from the corner: the grid starts 7px further in, so its first dot lands inside the pill's
   rounded end instead of just outside it, where it read as a stray mark (R59 L5) */
.sm--phone .sm-canvas {
  background-position: 7px 7px;
}

/* Until the pre-paint fit has run (or the page has mounted) the all level is not drawn: never at the server's guess of a
   scale (R59 L1). A shared link to a group, area or part never draws it at all: the canvas takes the target's tint and
   the map opens there on mount (R59 M1). */
.sm-canvas[data-fit-w]:not([data-fitted]):not([data-mounted]) .sm-layer--all,
.sm[data-sm-deep] .sm-layer--all {
  visibility: hidden;
}

.sm[data-sm-deep] .sm-canvas,
.sm[data-sm-deep] .sm-bar {
  background-color: color-mix(in srgb, var(--sm-deep-tint) 55%, rgb(var(--v-theme-light)));
}

/* A shared link's target, before the page takes over: the bar shows the target's crumbs (and an area's line), written
   by the pre-paint script, instead of the all level's count over a canvas that is not showing it (R61 N4). They are
   drawn in a copy of the page's own row (sm-crumb-row--pre), so the words sit where the page's will (R62 P1). Two
   classes on the hiding rule: the plain .sm-crumb-row rule further down would otherwise draw it. */
.sm-crumb-row.sm-crumb-row--pre {
  display: none;
  pointer-events: none;
}

.sm[data-sm-crumbs] .sm-crumb-row--pre {
  display: flex;
}

.sm[data-sm-crumbs] .sm-crumb-row:not(.sm-crumb-row--pre),
.sm[data-sm-crumbs] .sm-crumb-line,
.sm:not([data-sm-crumb-area]) .sm-pre-group,
.sm:not([data-sm-crumb-area]) .sm-pre-group-sep,
.sm[data-sm-crumb-area] .sm-pre-count {
  display: none;
}

.sm-pre-group::before {
  content: var(--sm-group);
}

.sm-pre-current .sm-crumb-text::before {
  content: var(--sm-current);
}

.sm-pre-count::before {
  content: var(--sm-count);
}

/* The current name wears the target's ink, as the page's does; the area icon's 18px is kept, drawn once the page is */
.sm-crumb-row--pre .sm-crumb--area {
  color: var(--sm-ink, var(--area-ink));
}

.sm-pre-art {
  display: block;
  flex: none;
  width: 18px;
  height: 18px;
}

.sm[data-sm-crumb-line] .sm-crumbs::after {
  content: var(--sm-line);
  font-size: 13px;
  line-height: 1.3;
  color: rgb(var(--v-theme-charcoal));
  margin: 0 0 4px 56px;
  max-width: calc(100% - 56px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The box remembered unticked: until the page has applied it, the server's ticked box and its all-level count (which
   counts the admin parts) are not shown rather than shown wrong (R61 N4) */
.sm[data-sm-admin-off] .sm-crumb-count:not(.sm-pre-count),
.sm[data-sm-admin-off] .sm-check :deep(.v-selection-control__input) {
  visibility: hidden;
}

/* Until the pre-paint script has marked the root (or the page is ready), the bar's count is not drawn: a frame
   painted between the bar's markup and the script can not show the all-level count over a deep link (R62 P2) */
.sm:not([data-sm-pre]):not(.sm--ready) .sm-bar .sm-crumb-count {
  visibility: hidden;
}

.sm-prefit {
  display: none;
}

.sm-layer {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
  will-change: transform;
  transition: opacity 480ms ease;

  /* Before the camera writes its own transform: the pre-paint fit, else the server's */
  &--all {
    transform: var(--sm-tf, var(--sm-tf-ssr));
  }

  /* The layer the camera has left glides to its resting place by CSS and fades out quickly: half the time the entered
     layer takes to fade in, so the two cross and no old words linger behind the new cards (R53 M2) */
  &--passive {
    transition: transform 480ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 220ms ease;
    pointer-events: none;
  }

  /* A card layer one or two levels up fades right out: no ghost words behind what was entered */
  &--receded {
    opacity: 0;
  }

  &--hidden {
    visibility: hidden;
  }

  &--leaving {
    opacity: 0;
  }

  /* The entered layer mounts transparent and fades in once the camera has placed it (two classes: this must win over the layer's own transition) */
  &.sm-layer--entering {
    opacity: 0;
    transition: none;
  }
}

.sm-layer--group,
.sm-layer--area {
  /* No opacity set here: the receded / leaving / entering states above must win (same specificity, so a 1 here would override them) */
  transition: opacity 360ms ease;

  &.sm-layer--passive {
    transition: transform 480ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 220ms ease;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sm-layer, .sm-layer--passive, .sm-canvas { transition: none; }
}

.sm-edges {
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
  pointer-events: none;
}

.smk {
  fill: none;
  transition: opacity 180ms ease, stroke 180ms ease;

  &-relation {
    stroke: rgba(26, 26, 26, 0.16);
    stroke-width: 3;
    stroke-linecap: round;

    &.is-hot {
      stroke: rgba(26, 26, 26, 0.55);
      stroke-width: 4;
    }

    &.is-faint {
      opacity: 0.35;
    }
  }

  &-hold {
    stroke: rgb(var(--v-theme-border));
    stroke-width: 2.2;

    &.is-hot {
      stroke: rgb(var(--v-theme-primary));
      stroke-width: 2.8;
    }
  }

  &-link {
    stroke: rgba(36, 119, 200, 0.5);
    stroke-width: 1.5;
    stroke-dasharray: 6 5;

    &.is-hot {
      stroke: rgb(var(--v-theme-primary));
      stroke-width: 2.4;
    }
  }

  /* A step: what comes before what, in the area's ink with an arrowhead. Strong
     enough to tell from the grey holds rails even where the ink is grey (R48 L6) */
  &-step {
    stroke: var(--area-ink);
    stroke-width: 2;
    opacity: 0.85;

    &.is-hot {
      stroke: rgb(var(--v-theme-primary));
      stroke-width: 2.4;
      opacity: 1;
    }
  }
}

.smk-arrow {
  fill: var(--area-ink);
  opacity: 0.85;

  &--hot {
    fill: rgb(var(--v-theme-primary));
    opacity: 1;
  }
}

.smk-label {
  font-size: var(--part-label-px, 12px);
  font-weight: 700;
  fill: rgb(var(--v-theme-charcoal));
  paint-order: stroke;
  stroke: rgb(var(--v-theme-light));
  stroke-width: 4px;
  stroke-linejoin: round;

  &--link {
    fill: rgb(var(--v-theme-info));
  }
}

.sm--area .smk-label {
  stroke: color-mix(in srgb, var(--area-tint) 55%, rgb(var(--v-theme-light)));
}

/* ── Chips tray: a grid of chip columns in free space beside the card ── */
.sm-tray {
  position: absolute;
  z-index: 4;
  display: grid;
  grid-template-columns: repeat(var(--tray-cols, 1), 232px);
  grid-template-rows: calc(var(--tray-cap, 20px) - 6px);
  grid-auto-rows: 34px;
  column-gap: 8px;
  row-gap: 6px;
  align-items: start;
  justify-items: start;
  pointer-events: auto;

  :deep(.smc) {
    width: 100%;
  }
}

/* The caption names the card the chips belong to, so a tray placed away from it still reads as its own */
.sm-tray-cap {
  grid-column: 1 / -1;
  align-self: end;
  font-size: 10.5px;
  line-height: 1;
  letter-spacing: 0.2px;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding-left: 4px;

  strong {
    color: rgb(var(--v-theme-text));
    font-weight: 800;
  }
}

.sm-tray--far .sm-tray-cap {
  color: rgb(var(--v-theme-primary));

  strong {
    color: rgb(var(--v-theme-primary));
  }
}

.sm-leader {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
  overflow: visible;

  line {
    stroke: rgb(var(--v-theme-primary));
    stroke-width: 1.5;
    stroke-dasharray: 3 4;
    stroke-linecap: round;
    opacity: 0.7;
  }

  circle {
    fill: rgb(var(--v-theme-primary));
  }
}

.sm-anchor {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

/* ── Phone dock: the selected part's chips above the bar ── */
.sm-dock {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(63px + env(safe-area-inset-bottom, 0px));
  z-index: 3;
  padding: 6px 0 8px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.sm-dock-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 4px 0 14px;
}

.sm-dock-title {
  font-size: 12px;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  strong {
    color: rgb(var(--v-theme-text));
  }
}

.sm-dock-row {
  display: flex;
  gap: 8px;
  padding: 2px 12px 4px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  :deep(.smc) {
    flex: none;
    max-width: 260px;
  }
}

.sm-tray-enter-active,
.sm-tray-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.sm-tray-enter-from,
.sm-tray-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.sm-tray-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 30px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px dashed rgb(var(--v-theme-border));
  background: rgba(255, 255, 255, 0.9);
  color: rgb(var(--v-theme-charcoal));
  font-size: 11.5px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background: #fff;
    border-style: solid;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
  }
}

.sm--phone .sm-tray-more {
  min-height: 44px;
  flex: none;
}

/* ── Desktop bar: one row (the crumbs grow, the tools never wrap); inside an area a second row for the area's line.
   The map measures the bar's height and fits everything under it (R53 M1). ── */
.sm-bar {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 3;
  padding: 12px 14px 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  /* Opaque, in the canvas's own colour, above every layer: a card panned or flown under it never shows through (R59 L4:
     an "Admin feature" label read faintly through the line row). The map fits everything under its measured height. */
  background-color: rgb(var(--v-theme-light));
  border-bottom: 1px solid rgba(26, 26, 26, 0.06);
  transition: background-color 480ms ease;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.sm--group .sm-bar,
.sm--area .sm-bar {
  background-color: color-mix(in srgb, var(--area-tint) 55%, rgb(var(--v-theme-light)));
}

.sm-crumbs {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
}

.sm-crumb-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  min-height: 42px;
}

.sm-back {
  display: inline-flex;
  flex: 0 0 auto;
  margin-right: 4px;
}

.sm-crumb {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgb(var(--v-theme-charcoal));
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: -0.2px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-text));
  }

  &:disabled {
    cursor: default;
  }

  &--current {
    color: rgb(var(--v-theme-text));
    font-size: 17px;
    font-weight: 800;
  }

  &--area {
    color: var(--area-ink);
    min-width: 0;

    :deep(.sma) {
      color: var(--area-ink);
      flex: none;
    }
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
  }
}

/* The current name may shorten with an ellipsis on a narrow canvas; the tools beside it never wrap */
.sm-crumb-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sm-crumb-sep {
  color: rgb(var(--v-theme-textSubtle));
}

.sm-crumb-count {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  font-variant-numeric: tabular-nums;
  margin-left: 6px;
  white-space: nowrap;
}

/* The area's one-line description, on its own row under the crumbs once you are inside it (it is not on its card);
   it starts under the All crumb's text and may run the crumbs' whole width */
.sm-crumb-line {
  font-size: 13px;
  line-height: 1.3;
  color: rgb(var(--v-theme-charcoal));
  margin: 0 0 4px 56px;
  max-width: calc(100% - 56px);
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sm-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 1 auto;
  flex-wrap: nowrap;
  justify-content: flex-end;
  min-width: 0;
  min-height: 42px;
}

.sm-search {
  position: relative;
  flex: 0 1 300px;
  width: 300px;
  min-width: 170px;
  max-width: 100%;

  :deep(.v-field) {
    background: rgb(var(--v-theme-surface));
    border-radius: 12px;
  }
}

.sm-results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  z-index: 5;
  padding: 6px;
  display: grid;
  gap: 2px;
  box-shadow: 0 12px 32px rgba(26, 26, 26, 0.12);
  max-height: 60vh;
  overflow-y: auto;
}

.sm-result {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: rgb(var(--v-theme-text));

  &:hover,
  &--active {
    background: rgb(var(--v-theme-light));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
  }
}

.sm-result-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sm-result-name {
  font-size: 13.5px;
  font-weight: 700;
}

.sm-result-sec {
  font-size: 11.5px;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sm-result-empty {
  padding: 8px 10px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.sm-areas-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px 0 10px;
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-border));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 1px;
  }
}

.sm-areas-menu {
  margin-top: 6px;
  padding: 6px;
  min-width: 280px;
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 6px 10px;
  box-shadow: 0 12px 32px rgba(26, 26, 26, 0.12);
  max-height: 80vh;
  overflow-y: auto;
}

/* The menu lists the areas under their groups, the same grouping as the map */
.sm-areas-group {
  display: grid;
  gap: 1px;
  align-content: start;
}

.sm-areas-group-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--group-ink, rgb(var(--v-theme-textSubtle)));
  padding: 8px 10px 4px;
}

.sm-areas-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 10px 7px 8px;
  border: none;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  font-family: inherit;
  color: rgb(var(--v-theme-text));
  cursor: pointer;

  &:hover {
    background: var(--chip-tint);
  }

  &--current {
    background: var(--chip-tint);
    font-weight: 800;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
  }
}

.sm-areas-art {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: var(--chip-tint);
  color: var(--chip-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  :deep(.sma) {
    color: var(--chip-ink);
  }
}

.sm-areas-name {
  flex: 1;
  font-size: 13.5px;
  font-weight: 700;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sm-areas-count {
  font-size: 11px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  color: var(--chip-ink, rgb(var(--v-theme-charcoal)));
  border: 1px solid rgba(26, 26, 26, 0.08);
  font-variant-numeric: tabular-nums;
}

.sm-areas-tick {
  color: var(--chip-ink);
}

.sm-check {
  flex: none;

  :deep(.v-label) {
    font-size: 13px;
    font-weight: 600;
    color: rgb(var(--v-theme-text));
    opacity: 1;
    white-space: nowrap;
  }

  :deep(.v-selection-control) {
    min-height: 40px;
  }
}

/* ── Phone: where you are ── */
.sm-phone-top {
  position: absolute;
  left: 10px;
  top: 10px;
  z-index: 3;
  max-width: calc(100% - 20px);
}

.sm-phone-crumb {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px 0 10px;
  border-radius: 999px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  color: rgb(var(--v-theme-text));
  font-size: 14px;
  font-weight: 800;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(26, 26, 26, 0.08);
  max-width: 100%;

  &--static {
    cursor: default;
    padding: 0 14px;
    font-weight: 700;
    color: rgb(var(--v-theme-charcoal));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
  }

  :deep(.sma) {
    color: var(--area-ink);
  }
}

.sm-phone-crumb-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sm-phone-crumb-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Inside an area the pill carries the area's line too (the line may take two lines; the map measures the pill and fits under it) */
.sm-phone-crumb--two {
  min-height: 56px;
  padding-top: 6px;
  padding-bottom: 6px;
  border-radius: 22px;
}

.sm-phone-crumb-line {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
  color: rgb(var(--v-theme-charcoal));
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

/* ── Zoom ── */
.sm-zoom {
  position: absolute;
  right: 14px;
  bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 2;

  :deep(.tl-iconbtn) {
    width: 36px;
    height: 36px;
    background: rgb(var(--v-theme-surface));
    box-shadow: 0 4px 14px rgba(26, 26, 26, 0.08);
  }
}

/* ── Phone bar ── */
.sm-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  gap: 6px;
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.sm-bottom-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 46px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  flex: 1 1 0;
  min-width: 0;

  &--wide {
    flex: 1.4 1 0;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
  }
}

.sm-bottom-btn--icon {
  flex: 0 0 52px;
  padding: 0;
}

.sm-bottom-current {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Phone sheets ── */
.sm-sheet-list {
  display: grid;
  gap: 2px;
  margin-top: 10px;
}

.sm-sheet-areas {
  display: grid;
}

.sm-sheet-group-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--group-ink, rgb(var(--v-theme-textSubtle)));
  padding: 14px 16px 6px;
  background: rgb(var(--v-theme-light));
}

.sm-sheet-area {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 8px 16px;
  border: none;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  text-align: left;
  font-family: inherit;
  color: rgb(var(--v-theme-text));
  cursor: pointer;

  &--current {
    background: var(--chip-tint);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
  }
}

.sm-sheet-area-art {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: var(--chip-tint);
  color: var(--chip-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  :deep(.sma) {
    color: var(--chip-ink);
  }
}

.sm-sheet-area-name {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sm-sheet-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px 10px 8px;
}
</style>

<style lang="scss">
/* Not scoped: the dialogs teleport out of the component. On a phone every map
   dialog docks to the bottom as a sheet; on desktop it is the ordinary TlDialog. */
@media (max-width: 959px) {
  .v-overlay__content.smd-sheet {
    align-self: flex-end;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 78dvh;

    .tl-dialog {
      border-radius: 18px 18px 0 0;
      max-height: 78dvh;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
  }
}
</style>
